import {safeHtml, safeText} from "../../../util/CommonUtils.js";
import VisualizationSelectors from "../VisualizationSelectors.js";
import {COLUMN_TYPE} from "../TableColumn.js";

export class VTableComponent {
	static #DEFAULT_DISPLAY_OPTIONS = {
		rowHeightPx: 24,
		tableHeightPx: 500,
	};
	/** @type {VisualizationDataSelection} */
	#selectionManager;

	/** @param {VisualizationDataSelection} selectionManager */
	constructor(selectionManager) {
		this.#selectionManager = selectionManager;
		this._callbacks = [];
		this._body = new VTableBody();
	}

	/**
	 * @param {HTMLElement} parentNode
	 * @param {VizData} vizData
	 * @param {{[rowHeightPx]: number, [tableHeightPx]: number}} [displayOptions]
	 * @returns {VTableComponent}
	 */
	render(parentNode, vizData, displayOptions) {
		parentNode.appendChild(VTableComponent._htmlTemplate());
		/** @type {HTMLElement} */
		this._root = parentNode.lastChild;
		this._vizData = vizData;
		this._header = new VTableHeader(this);
		displayOptions = Object.assign({}, VTableComponent.#DEFAULT_DISPLAY_OPTIONS, displayOptions);
		this._body.render(this, displayOptions, this.#selectionManager);

		this._body.on("check-comparing", this.#selectionManager.getBackgroundColor.bind(this.#selectionManager));

		this._body.on('selection-changed', (data) => {
			if (Array.isArray(this._callbacks['selection-changed'])) {
				this._callbacks['selection-changed'].forEach(callback => {
					callback(data);
				});
			}
		});

		this._header.on('column-click', (columnIndex) => {
			if (Array.isArray(this._callbacks['header-click'])) {
				this._callbacks['header-click'].forEach(callback => {
					callback(columnIndex);
				});
			}
		});

		this._header.on('columnWidth-changed', (columnIndex, width) => {
			if (Array.isArray(this._callbacks['columnWidth-changed'])) {
				this._callbacks['columnWidth-changed'].forEach(callback => {
					callback(columnIndex, width);
				});
			}
		});

		this._body.on('rowHeight-changed', (rowHeightPx) => {
			if (Array.isArray(this._callbacks['rowHeight-changed'])) {
				this._callbacks['rowHeight-changed'].forEach(callback => {
					callback(rowHeightPx);
				});
			}
		});
		return this;
	}

	/** @return {VizData} */
	get vizData() {
		return this._vizData;
	}

	onRowClick(callback) {
		this._body.on('row-click', (rowIndex, cellIndex, rowSelector, evt) => {
			const rowObject = this._vizData.getRowObject(rowIndex);
			callback(rowIndex, cellIndex, rowObject.id, rowSelector, evt);
		});
	}

	onRowHover(callback){
		this._body.on('on-row-mouse-hover', (rowObjectId, isHovered)=> {
			/**
			 * @param rowObjectId - in case if each row in the table is injection then it is the injection id,
			 *    			 if each row is a "peak-row" then the id is a combination of injectionId and peakId
			 * @param isHovered - boolean
			 */
			callback(rowObjectId, isHovered);
		});
	}

	on(eventName, callback) {
		if (!Array.isArray(this._callbacks[eventName]))
			this._callbacks[eventName] = [];
		this._callbacks[eventName].push(callback);
	}

	onDelete() {
		let rowSelector = this._body._rowSelector;
		rowSelector._selection.removeListener(rowSelector);
	}
	isRendered(){
		return this._root != null;
	}

	markCellAsSelected(rowIndex, cellIndex) {
		this._body.markCellAsSelected(rowIndex, cellIndex);
	}
	getRowIndexWithActiveCell() {
		return this._body.getRowIndexWithActiveCell();
	}

	setActiveRow(rowIndex) {
		this._body.setActiveRow(rowIndex);
	}
	getActiveRowIndex() {
		return this._body.getActiveRow();
	}

	updateCompareSelection(){
		this._body.removeAllComparedColors();
		for (const [key, value] of this.#selectionManager.getComparedInjections()){
			const findIndex = this.vizData._getActualRows().findIndex(r=>r.id === key);
			this._body.setComparedColor(findIndex, value.color);
		}
	}

	/** @param {number|null} index */
	setLastClickedRowId(index){
		this._body.setLastClickedRowId(index)
	}

	unselectActiveCell(){
		this._body.unselectActiveCell()
	}

	navigationKeyPressed(data, successNavigationCallback) {
		const currentRowIndex = this.getActiveRowIndex();
		if (currentRowIndex < 0)
			return;
		const isBottomDirection = data.direction === 'ArrowDown';
		const navigateThroughSelected = data.highlighted;
		const targetRowIndex = this.#getNavigationTargetRowIndex(currentRowIndex, isBottomDirection, navigateThroughSelected);
		if (targetRowIndex < 0)
			return;
		const rows = this._vizData._getActualRows();
		if (typeof successNavigationCallback === 'function')
			successNavigationCallback(rows[targetRowIndex].id);
	}

	#getNavigationTargetRowIndex(currentRowIndex, isBottomDirection, navigateThroughSelectedRowsOnly) {
		const rows = this._vizData._getActualRows();
		const step = isBottomDirection ? 1 : -1;
		if (!navigateThroughSelectedRowsOnly) {
			const targetRow = currentRowIndex + step;
			if (targetRow >= 0 && targetRow < rows.length)
				return targetRow;
			else return -1;
		} else {
			for (let i = currentRowIndex + step; i >= 0 && i < rows.length; i += step) {
				if (this.#selectionManager.test(rows[i].id))
					return i;
			}
		}
		return -1;
	}

	static _htmlTemplate(){
		let template = document.createElement('template');
		template.innerHTML = `<div class="v-table">
 				<div class="v-table-header"></div>
 				<div class="v-table-body">
 					<div class="full-height">
 						<div class="v-table-rows"></div>
 					</div>
 				</div>
			 </div>`;
		return template.content;
	}
}

class VTableHeader {
	/**
	 * @param {VTableComponent} table
	 */
	constructor(table) {
		this._table = table;
		this._callbacks = [];

		const vizData = table.vizData;

		vizData.on('column-added', this._addColumn.bind(this));
		vizData.on('column-updated', this._updateColumn.bind(this));
		vizData.on('column-moved', this._moveColumn.bind(this));
		vizData.on('column-deleted', this._deleteColumn.bind(this));
		vizData.on('full-change', this._reRenderColumns.bind(this));

		for (let column of vizData.getColumns())
			this._addColumn(column);

		let mouseTarget;
		table._root.querySelector('.v-table-header').addEventListener('mousedown', (evt) => {
			mouseTarget = evt.target;
			if (evt.button === 0 && evt.target.classList.contains('th-w-control')) {
				const index = Array.from(table._root.querySelectorAll('.v-table-header-cell')).indexOf(evt.target.parentNode);
				new ColWidthChangeIntent_v2(evt.target.parentNode, (width) => {
					vizData.updateColumn(index, {width});
					if (Array.isArray(this._callbacks['columnWidth-changed'])) {
						for (let callback of this._callbacks['columnWidth-changed'])
							callback(index, width);
					}
				}).start(evt);
			}
		}, true);
		table._root.querySelector('.v-table-header').addEventListener('click', (evt) => {
			if (evt.target.classList.contains('v-table-header-cell') && mouseTarget === evt.target) {
				if (Array.isArray(this._callbacks['column-click'])) {
					const index = Array.from(table._root.querySelectorAll('.v-table-header-cell')).indexOf(evt.target);
					for (let callback of this._callbacks['column-click'])
						callback(index);
				}
			}
		}, true);
	}

	on(eventName, callback) {
		if (!Array.isArray(this._callbacks[eventName]))
			this._callbacks[eventName] = [];
		this._callbacks[eventName].push(callback);
	}

	_addColumn(column) {
		const headerRow = this._table._root.querySelector(".v-table-header");
		headerRow.appendChild(this._headerCellTemplate(column));
		const element = headerRow.lastChild;
		if (column.getMissingDependencies().length > 0)
			element.classList.add('v-table-header-cell-missing-dependencies');
		this._makeDraggable(element);
	}

	_moveColumn(columnIndex, newIndex) {
		const headerRow = this._table._root.querySelector(".v-table-header");
		const targetIndex = columnIndex < newIndex ? newIndex + 1 : newIndex;
		headerRow.insertBefore(headerRow.children[columnIndex], headerRow.children[targetIndex]);
	}

	_deleteColumn(columnIndex) {
		const headerRow = this._table._root.querySelector(".v-table-header");
		const headerCell = headerRow.children[columnIndex];
		headerRow.removeChild(headerCell);
	}

	_updateColumn(columnIndex, column) {
		const {name, width, type} = column.getDisplayOptions();
		const headerRow = this._table._root.querySelector(".v-table-header");
		const headerCell = headerRow.children[columnIndex];
		headerCell.title = name;
		headerCell.style.width = width;
		headerCell.dataset.type = type;
		headerCell.querySelector(".v-table-header-cell-name").textContent = name;
		if (column.getMissingDependencies().length > 0)
			headerCell.classList.add("v-table-header-cell-missing-dependencies");
		else
			headerCell.classList.remove("v-table-header-cell-missing-dependencies");
	}

	_reRenderColumns() {
		const columns = this._table.vizData.getColumns();
		const header = this._table._root.querySelector(".v-table-header");
		const cellsRendered = header.children.length;
		if (cellsRendered > columns.length())
			for (let i = columns.length(); i < cellsRendered; i++)
				header.removeChild(header.lastChild);
		for (let i = 0; i < columns.length(); i++) {
			const column = columns.getByIndex(i);
			if (i >= cellsRendered)
				this._addColumn(column);
			else
				this._updateColumn(i, column);
		}
	}

	_makeDraggable(element) {
		element.setAttribute('draggable', true);
		element.addEventListener('dragend', (evt) => {
			evt.preventDefault();
			this._draggableColumn = null;
		})
		element.addEventListener('dragover', (evt) => {
			evt.preventDefault();
			evt.dataTransfer.dropEffect = "move";
		})
		element.addEventListener('dragstart', (evt) => {
			this._draggableColumn = element;
		})
		element.addEventListener('drop', async (evt) => {
			const el = this._draggableColumn;
			const target = evt.target.classList.contains('v-table-header-cell') ? evt.target : evt.target.closest('.v-table-header-cell');
			if (target === el || !el) return;
			let arr = Array.from(this._table._root.querySelectorAll('.v-table-header-cell'));
			let from = arr.indexOf(el);
			let to = arr.indexOf(target);
			this._table.vizData.moveColumn(from, to);
		})
	}

	_headerCellTemplate(column) {
		const {name, width, type} = column.getDisplayOptions();
		let template = document.createElement('template');
		template.innerHTML = safeHtml`<div class="v-table-header-cell" data-type="${type}" style="width: ${width}" title="${name}">
 					<span class="v-table-header-cell-name">${name}</span>
 					<div class="th-w-control"></div>
 			 	</div>`;
		return template.content;
	}
}

class VTableBody {
	constructor() {
		this._callbacks = [];
	}

	/**
	 * @param {VTableComponent} table
	 * @param displayOptions
	 * @param selection
	 */
	render(table, displayOptions, selection) {
		this._table = table;
		this._vizData = table.vizData;
		this._rowSelector = new RowSelector_v2(table, selection);
		this._params = new TableBodyParams(displayOptions.tableHeightPx, displayOptions.rowHeightPx, this._vizData.getRowsCount());
		this._rowsContainer = table._root.querySelector('.v-table-rows');
		this._firstRowIndex = 0;
		this._state = {
			scroll: {
				top: 0,
				left: 0
			}
		}
		this._activeRowIndex = null;

		this._vizData.on('column-added', (column) => {
			this._addColumn(column);
		});
		this._vizData.on('column-deleted', (columnIndex) => {
			this._deleteColumn(columnIndex);
		});
		this._vizData.on('column-moved', (columnIndex, newIndex) => {
			this._moveColumn(columnIndex, newIndex);
		});
		this._vizData.on('column-updated', (columnIndex, column) => {
			this._updateColumn(columnIndex, column);
		});
		this._vizData.on('full-change', () => {
			this._onRowsChanged();
		});

		table._root.addEventListener('scroll', (evt) => {
			this._state.scroll.left = evt.target.scrollLeft;
			this._onTableWidthChange()
		});

		new ResizeObserver(_ => this._onTableWidthChange()).observe(table._root);

		new IntersectionObserver((entries, observer) => {
			entries.forEach(entry => {
				if (entry.isIntersecting) {
					table._root.scrollLeft = this._state.scroll.left;
					table._root.querySelector('.v-table-body').scrollTop = this._state.scroll.top;
				}
			});
		}).observe(table._root);

		table._root.querySelector('.v-table-body').addEventListener('scroll', (evt) => {
			this._state.scroll.top = evt.target.scrollTop;
			this._onVerticalScroll(evt);
		});

		let mouseTarget = null;
		table._root.querySelector('.v-table-body').addEventListener('mousedown', (evt) => {
			mouseTarget = evt.target;
			if (evt.button === 0 && evt.target.classList.contains('tr-h-control')) {
				const offset = (table._root.querySelector('.v-table-body').getBoundingClientRect().top - evt.target.parentNode.getBoundingClientRect().top);
				new RowHeightChangeIntent_v2(evt.target.parentNode, (height) => {
					this._onRowHeightChanged(height);
					this._scrollTo(evt.target.parentNode.id, offset);
				}).start(evt);
			}
		}, true);
		table._root.querySelector('.v-table-body').addEventListener('click', (evt) => {
			const row = evt.target.closest('.v-table-body-row-cells')
				? evt.target.closest('.v-table-body-row') : null;
			if (row == null){
				// We clicked on the border
				return;
			}

			const cell = evt.target.closest('.v-table-body-row-cell');
			const cellIndex = Array.from(row.querySelector('.v-table-body-row-cells').children).indexOf(cell);
			if (Array.isArray(this._callbacks['row-click']))
				for (const callback of this._callbacks['row-click'])
					callback(+row.id, cellIndex, this._rowSelector, evt);
		}, true);

		this._onRowsChanged();
	}

	/**
	 * @param {number} rowIndex
	 * @param {number} cellIndex
	 */
	markCellAsSelected(rowIndex, cellIndex) {
		const cell = this._table._root
			.querySelector(`.v-table-body-row[id="${rowIndex}"] .v-table-body-row-cell:nth-child(${cellIndex + 1})`);
		if (cell)
			this.#markCellAsSelected(cell);
	}

	/** @param {number|null} index */
	setLastClickedRowId(index){
		this._rowSelector.setLastClickedRowId(index)
	}

	unselectActiveCell(){
		const selectedEl = this.#getCellWithSelectedClass();
		if (selectedEl)
			this.#toggleCellSelection(false, selectedEl)
	}

	setActiveRow(rowIndex) {
		const fromIndex = this._activeRowIndex || 0;
		this._activeRowIndex = rowIndex;
		// 1. Deactivate the current active row if it exists.
		const currentActive = this._table._root.querySelector('.v-table-body-row.row-active');
		if (currentActive)
			currentActive.classList.remove('row-active');
		// 2. Find the row to be activated among the rendered rows.
		let toActivate = this._table._root.querySelector(`.v-table-body-row[id="${rowIndex}"]`);
		if (toActivate){
			toActivate.classList.add('row-active');
		} else {
			// The row to be activated is not rendered; it means that we must render it first
			// Since we know the row index, we can find how many rows we must re-render to make our row visible.
			let offsetToFirstRenderedRow = 0;
			const rowsTotal = this._vizData.getRowsCount();
			const rowsRendered = this._params.rowsRendered;
			const rowsInView = this._params.rowsInView;
			if (rowIndex < rowsRendered) {
				offsetToFirstRenderedRow = 0;
			} else if (rowIndex >= rowsTotal - rowsRendered) {
				offsetToFirstRenderedRow = rowsTotal - rowsRendered;
			} else {
				offsetToFirstRenderedRow = rowIndex - rowsInView;
			}
			this._firstRowIndex = offsetToFirstRenderedRow;
			this._table._root.querySelectorAll('.v-table-body-row').forEach(row => {
				row.setAttribute('id', offsetToFirstRenderedRow);
				this._fillRow(offsetToFirstRenderedRow);
				offsetToFirstRenderedRow++;
			});
			toActivate = this._table._root.querySelector(`.v-table-body-row[id="${rowIndex}"]`);
		}
		if (toActivate) {
			const bodyRect = this._table._root.querySelector('.v-table-body').getBoundingClientRect();
			const rowRect = toActivate.getBoundingClientRect();
			if (bodyRect.top <= rowRect.top && rowRect.bottom <= bodyRect.bottom) {
				// do nothing, the row is already in the visible part of the table
			} else {
				if (fromIndex < rowIndex) { // scroll bottom
					this._scrollTo(rowIndex, Math.min(0, -(bodyRect.height - rowRect.height)));
				} else {
					this._scrollTo(rowIndex, 0);
				}
			}
		}
	}

	/** @return {number} */
	getRowIndexWithActiveCell() {
		const cell = this.#getActiveSelectedCell();
		if (cell == null)
			return -1;
		const row = cell.closest(".v-table-body-row");
		if (row == null)
			return -1;
		return +row.id;
	}

	getActiveRow(){
		return this._activeRowIndex;
	}

	/**
	 * @param {string} rowIndex
	 * @param {string} color
	 */
	setComparedColor(rowIndex, color){
		const toActivate = this._table._root.querySelector(`.v-table-body-row[id="${rowIndex}"]`);
		if (toActivate) {
			toActivate.classList.add('compared');
			toActivate.style.background = color;
		}
	}

	removeAllComparedColors(){
		this._table._root.querySelectorAll(`.v-table-body-row`).forEach(row => {
			row.style.background = '';
			row.classList.remove('compared')
		});
	}

	on(eventName, callback) {
		if (!Array.isArray(this._callbacks[eventName]))
			this._callbacks[eventName] = [];
		this._callbacks[eventName].push(callback);
	}

	/**
	 * @param {boolean} isHovered
	 * @param {Event} evt
	 * @private
	 */
	_onRowMouseHover(isHovered, evt){
		const rowId = evt.target.closest('.v-table-body-row').getAttribute('id');
		// In visualisations id can be just injection id (in injection case) or injection id + peak id (in peak case)
		// We use name rowObjectId because it is not always injection id
		const rowObjectId = this._vizData.getRowObject(rowId).id;
		if (Array.isArray(this._callbacks['on-row-mouse-hover']))
			for (let callback of this._callbacks['on-row-mouse-hover'])
				callback(rowObjectId, isHovered);
	}

	_onTableWidthChange() {
		this._table._root.querySelector('.v-table-body').style.width = this._table._root.getBoundingClientRect().width
			+ this._table._root.scrollLeft + 'px';
	}

	_addColumn(column) {
		const columnIndex = this._vizData.getColumns().getIndexedColumns([column.getDisplayName()]).indices[0];
		this._table._root.querySelectorAll('.v-table-body-row-cells').forEach(row => {
			if (columnIndex >= row.children.length) {
				const {type, width} = column.getDisplayOptions();
				row.appendChild(this._cellTemplate(type, width));
			}
		});
		this._updateColumn(columnIndex, column);
	}

	_updateColumn(columnIndex, column) {
		const {type, width} = column.getDisplayOptions();
		this._table._root.querySelectorAll('.v-table-body-row-cells').forEach(row => {
			const rowId = row.parentElement.getAttribute('id');
			const cell = row.children[columnIndex];
			cell.setAttribute('data-type', type);
			cell.style.width = width;
			this._fillCell(cell, this._vizData.getCell(rowId, columnIndex));
		});
	}

	_moveColumn(columnIndex, newIndex) {
		const targetIndex = columnIndex < newIndex ? newIndex + 1 : newIndex;
		this._table._root.querySelectorAll('.v-table-body-row-cells').forEach(row => {
			row.insertBefore(row.children[columnIndex], row.children[targetIndex])
		});
	}

	_deleteColumn(columnIndex) {
		this._table._root.querySelectorAll('.v-table-body-row-cells').forEach(row => {
			row.removeChild(row.children[columnIndex]);
		});
	}

	_scrollTo(index, offsetPx) {
		this._table._root.querySelector('.v-table-body').scroll({
			top: this._params.rowHeightPx * index + offsetPx
		})
	}

	_onVerticalScroll() {
		const currentOffset = this._getCurrentOffset();
		const rowsScrolled = this._firstRowIndex - currentOffset;
		const rowsToUpdate = Math.min(Math.abs(rowsScrolled), this._params.rowsRendered);
		this._rowsContainer.style.transform = `translateY(${currentOffset * this._params.rowHeightPx}px)`;

		if (rowsToUpdate < 1)
			return;
		rowsScrolled < 0
			? this._onScrollBottom(rowsToUpdate, currentOffset)
			: this._onScrollTop(rowsToUpdate, currentOffset);
		this._firstRowIndex = currentOffset;
	}

	_onScrollBottom(rowsToUpdate, firstRowIdx) {
		const start = firstRowIdx + this._params.rowsRendered - rowsToUpdate;
		for (let i = 0; i < rowsToUpdate; i++) {
			const row = this._rowsContainer.firstChild;
			let rowId = start + i;
			row.setAttribute('id', rowId);
			this._rowsContainer.append(row);
			this._fillRow(rowId);
		}
	}

	_onScrollTop(rowsToUpdate, firstRowIdx) {
		const start = firstRowIdx + rowsToUpdate - 1;
		for (let i = 0; i < rowsToUpdate; i++) {
			const row = this._rowsContainer.lastChild;
			let rowId = start - i;
			row.setAttribute('id', rowId);
			this._rowsContainer.prepend(row);
			this._fillRow(rowId);
		}
	}

	_getCurrentOffset() {
		const rowsScrolled = Math.floor(this._table._root.querySelector('.v-table-body').scrollTop / this._params.rowHeightPx);
		const maxOffset = this._vizData.getRowsCount() - this._params.rowsInView * 2;
		return Math.max(0, Math.min(rowsScrolled, maxOffset) - this._params.rowsInView);
	}

	_setRenderedRowsCount(count) {
		const rows = [...this._table._root.querySelectorAll('.v-table-body-row')];
		if (count < rows.length) {
			for (let i = rows.length - 1; i >= count; i--)
				rows[i].remove();
		} else if (count > rows.length) {
			for (let i = rows.length; i < count; i++)
				this._addRow();
		}
	}

	_addRow() {
		const rows = [...this._table._root.querySelectorAll('.v-table-body-row')];
		let rowIndex = this._firstRowIndex + rows.length;
		let insertRow = this._rowsContainer.append;
		if (rowIndex >= this._vizData.getRowsCount()) {
			rowIndex = this._firstRowIndex - 1;
			if (rowIndex < 0)
				throw new Error('No more rows to add');
			this._firstRowIndex = rowIndex;
			insertRow = this._rowsContainer.prepend;
		}
		const template = this._rowTemplate(rowIndex);
		insertRow.call(this._rowsContainer, template);
		this._fillRow(rowIndex);
		this._rowsContainer.querySelector('.v-table-body-row[id="' + rowIndex + '"]').addEventListener('mouseenter', (evt) => {
			this._onRowMouseHover(true, evt);
		})
		this._rowsContainer.querySelector('.v-table-body-row[id="' + rowIndex + '"]').addEventListener('mouseleave', (evt) => {
			this._onRowMouseHover(false, evt);
		})
	}

	_fillRow(rowIndex) {
		const rowData = this._vizData.getCells(rowIndex);
		const row = this._rowsContainer.querySelector(`.v-table-body-row[id="${rowIndex}"]`);
		row.querySelectorAll(`.v-table-body-row-cell`).forEach((cell, index) => {
			this._fillCell(cell, rowData[index]);
		});
		if (this._rowSelector.isSelected(row.id))
			row.classList.add('row-selected');
		else
			row.classList.remove('row-selected');
		row.style.background = this.#getBackgroundColor(this._vizData.getRowObject(rowIndex).id);
		row.classList.toggle('row-active', this._activeRowIndex === rowIndex);
		row.classList.toggle('compared', this._rowSelector.isInComparison(rowIndex));
	}

	/**
	 * @param {HTMLElement} cell
	 * @param {{value: *, type: string}} data
	 */
	_fillCell(cell, data) {
		cell.removeAttribute('title');

		if (data.value instanceof Promise) {
			cell.innerHTML = '';
			const rowId = cell.parentElement.parentElement.getAttribute('id');
			data.value.then(value => {
				if (cell.parentElement.parentElement.getAttribute('id') === rowId)
					cell.innerHTML = value;
			})
		} else {
			const value = data.value;
			cell.innerHTML = '';
			const span = document.createElement('span');
			span.textContent = value;
			span.classList.add('v-table-body-row-cell__data')
			cell.append(span);
			const type = cell.getAttribute("data-type");
			if (type === COLUMN_TYPE.MIXED || type === COLUMN_TYPE.UNKNOWN || !value) // do not validate these values
				return
			if (type !== COLUMN_TYPE.resolve(value)){
				cell.insertAdjacentHTML("afterbegin",
					`<span class="material-symbols-outlined v-table__unmatched-icon">warning</span>`);
				cell.title = `The value ${safeText(value.toString())} doesn’t correspond to the column type ${type}`
			}
		}
	}

	/**
	 * @param id
	 * @returns {*}
	 */
	#getBackgroundColor(id) {
		if (this._callbacks['check-comparing'])
			return this._callbacks['check-comparing'][0](id)
	}

	/** @param {HTMLElement} cell */
	#markCellAsSelected(cell){
		const selectedEl = this.#getCellWithSelectedClass();
		if (selectedEl)
			this.#toggleCellSelection(false, selectedEl)
		if (cell !== selectedEl)
			this.#toggleCellSelection(true, cell)
	}

	/**
	 * @param {boolean} isSelected
	 * @param {HTMLElement?} cell
	 */
	#toggleCellSelection(isSelected, cell ){
		cell.classList.toggle(VisualizationSelectors.CLASS_NAMES.selectedCellClassName, isSelected)
	}

	/** @returns {HTMLElement} */
	#getActiveSelectedCell(){
		return this._table._root.querySelector(`${VisualizationSelectors.CLASS_NAMES.activeCellInSelectedRow}`);
	}

	/**
	 * When we scroll table, and want to change cell selection, we search selected well without row-selection class
	 * because row selector already is not actual
	 *
	 * @returns {HTMLElement}
	 */
	#getCellWithSelectedClass(){
		return this._table._root.querySelector(`.${VisualizationSelectors.CLASS_NAMES.selectedCellClassName}`);
	}

	_onRowsChanged() {
		this._firstRowIndex = 0;
		this._table._root.querySelectorAll('.v-table-body-row').forEach(row => row.remove());
		this._params.setRowsTotal(this._vizData.getRowsCount());
		this._table._root.querySelector('.v-table-body').scrollTop = 0;
		this._table._root.querySelector('.full-height').style.height = this._params.virtualHeight + 'px';
		this._setRenderedRowsCount(this._params.rowsRendered);
		this._table._root.querySelectorAll('.v-table-body-row').forEach(row => this._fillRow(+row.id));
	}

	_onRowHeightChanged(rowHeightPx) {
		this._params.setRowHeight(rowHeightPx);
		this._setRenderedRowsCount(this._params.rowsRendered);
		this._table._root.querySelector('.full-height').style.height = this._params.virtualHeight + 'px';
		let height = this._params.rowHeightPx + 'px';
		this._table._root.querySelectorAll('.v-table-body-row').forEach((row) => {
			row.style.height = height;
			row.style.lineHeight = height;
		});
		if (Array.isArray(this._callbacks['rowHeight-changed'])) {
			for (let callback of this._callbacks['rowHeight-changed'])
				callback(height);
		}
	}

	_rowTemplate(rowIndex) {
		const cells = this._vizData.getColumns()._columns.map((c) => {
			const {type, width} = c.getDisplayOptions();
			return `<div class="v-table-body-row-cell" data-type="${type}" style="width: ${width}"></div>`
		}).join('');
		const tmpl = document.createElement('template');
		tmpl.innerHTML = `<div id="${rowIndex}" class="v-table-body-row" style="height: ${this._params._rowHeightPx}px; line-height: ${this._params._rowHeightPx}px;" data-order="${(+rowIndex % 2) === 0 ? 'even' : 'odd'}">
					<div class="v-table-body-row-cells">${cells}</div>
					<div class="tr-h-control"></div>
				</div>`;
		return tmpl.content;
	}

	_cellTemplate(type, width) {
		const tmpl = document.createElement('template');
		tmpl.innerHTML = `<div class="v-table-body-row-cell" data-type="${type}" style="width: ${width}"></div>`;
		return tmpl.content;
	}
}

class TableBodyParams {
	constructor(tableHeight, rowHeight, rowsTotal) {
		this._tableHeightPx = tableHeight;
		this._rowHeightPx = rowHeight;
		this._rowsTotal = rowsTotal;
		this._virtualHeight = rowsTotal * rowHeight;
		this._rowsInView = Math.ceil(tableHeight / rowHeight);
		this._rowsRendered = Math.min(rowsTotal, this._rowsInView * 3);
	}

	get virtualHeight() {
		return this._virtualHeight;
	}

	get rowsRendered() {
		return this._rowsRendered;
	}

	get rowHeightPx() {
		return this._rowHeightPx;
	}

	get rowsInView() {
		return this._rowsInView;
	}

	setRowsTotal(rowsTotal) {
		this._rowsTotal = rowsTotal;
		this._virtualHeight = rowsTotal * this._rowHeightPx;
		this._rowsRendered = Math.min(rowsTotal, this._rowsInView * 3);
	}

	setRowHeight(rowHeight) {
		this._rowHeightPx = rowHeight;
		this._virtualHeight = this._rowsTotal * rowHeight;
		this._rowsInView = Math.ceil(this._tableHeightPx / rowHeight);
		this._rowsRendered = Math.min(this._rowsTotal, this._rowsInView * 3);
	}

	setTableHeight(tableHeight) {
		this._tableHeightPx = tableHeight;
		this._rowsInView = Math.ceil(tableHeight / this._rowHeightPx);
		this._rowsRendered = Math.min(this._rowsTotal, this._rowsInView * 3);
	}
}

class RowHeightChangeIntent_v2 {
	constructor(row, cb) {
		this._row = row;
		this._cb = cb;
		this._rowH = parseInt(window.getComputedStyle(this._row).height, 10);

		this._onResize = (evt) => {
			evt.preventDefault();
			evt.stopPropagation()
			let height = Math.max(24, this._rowH += evt.movementY) + 'px';
			this._row.style.height = height;
			this._row.style.lineHeight = height;
		}

		this._onEnd = async (evt) => {
			evt.preventDefault();
			evt.stopPropagation()
			window.removeEventListener('mousemove', this._onResize)
			window.removeEventListener('mouseup', this._onEnd);
			this._cb(this._row.getBoundingClientRect().height);
		}
	}

	start(evt) {
		evt.preventDefault()
		evt.stopPropagation()
		window.addEventListener('mousemove', this._onResize)
		window.addEventListener('mouseup', this._onEnd)
	}
}

class ColWidthChangeIntent_v2 {
	constructor(column, cb) {
		this._column = column;
		this._cb = cb;
		this._width = parseInt(window.getComputedStyle(column).width, 10);

		this._onResize = (evt) => {
			evt.preventDefault();
			evt.stopPropagation();
			this._column.style.width = Math.max(50, this._width += evt.movementX) + 'px';
		}

		this._onEnd = async (evt) => {
			evt.preventDefault();
			evt.stopPropagation()
			window.removeEventListener('mousemove', this._onResize)
			window.removeEventListener('mouseup', this._onEnd)
			this._cb(this._column.getBoundingClientRect().width + 'px');
		}
	}

	start(evt) {
		evt.preventDefault()
		evt.stopPropagation()
		window.addEventListener('mousemove', this._onResize)
		window.addEventListener('mouseup', this._onEnd)
	}
}


class RowSelector_v2 {

	constructor(table, selection) {
		this._htmlTable = table._root;
		this._vizData = table.vizData;
		this._lastClickedRowIdx = null;
		this._selection = selection;
		selection.addListener(this);
	}

	/** @param {number|null} index */
	setLastClickedRowId(index){
		this._lastClickedRowIdx = index
	}

	/**
	 *
	 * @param {number} rowIdx
	 * @param {MouseEvent} evt
	 * @param {boolean} isRowAlreadySelected
	 */
	getSelectionAfterClick(rowIdx, evt, isRowAlreadySelected) {
		let added = new Map(), removed = new Map();
		const id = this._vizData.getRowObject(rowIdx).id.split('-');
		let k = id[0];
		let v = new Set();
		if (id.length > 1)
			v.add(id[1])
		if (evt.shiftKey && !(isRowAlreadySelected && rowIdx === this._lastClickedRowIdx)) {
			if (this._lastClickedRowIdx === null) {
				added = this.getRowObjectIds(rowIdx, rowIdx)
			} else {
				const from = Math.min(rowIdx, this._lastClickedRowIdx);
				const to = Math.max(rowIdx, this._lastClickedRowIdx);
				if (!isRowAlreadySelected) {
					added = this.getRowObjectIds(from, to);
				}
				else
					removed = this.getRowObjectIds(from, to);
			}
		} else if (evt.ctrlKey || evt.metaKey) {
			if (isRowAlreadySelected) {
				removed.set(k, v);
			} else {
				added.set(k, v);
			}
		} else {
			// means all other (rows, points, cells) should be deselected.
			// Setting 'removed' to undefined instead of putting actually deselected rows should fix the next problems:
			// - not all rows will be reachable when we implement pagination
			// - table can have filter, and some rows would not be shown at all
			removed = undefined;
			if (!isRowAlreadySelected) {
				added.set(k, v);
			}
		}
		if (added.size > 0)
			this._lastClickedRowIdx = rowIdx;
		else
			this._lastClickedRowIdx = null;
		return {
			added: added,
			removed: removed,
			target: this
		};
	}

	/**
	 * @param {number} rowIndex
	 * @return {boolean}
	 */
	isSelected(rowIndex) {
		return this._selection.test(this._vizData.getRowObject(rowIndex).id);
	}

	/**
	 * @param {number} rowIndex
	 * @return {boolean}
	 */
	isInComparison(rowIndex) {
		const rowObjectId = this._vizData.getRowObject(rowIndex).id;
		return this._selection.getComparedInjections().has(rowObjectId);
	}

	selectionChanged(selection, opts = {}) {
		this._htmlTable.querySelectorAll('.v-table-body-row').forEach(row => {
			if (this.isSelected(row.id))
				row.classList.add('row-selected');
			else
				row.classList.remove('row-selected')
		});
		if (opts.target !== this)
			this._lastClickedRowIdx = null;
	}

	/**
	 * @param {number} from
	 * @param {number?} to
	 * @private
	 */
	getRowObjectIds(from, to = 1 << 30) {
		const map = new Map();
		from = Math.max(from, 0);
		to = Math.min(to, this._vizData.getRowsCount() - 1);
		for (let i = from; i <= to; i++) {
			const id = this._vizData.getRowObject(i).id.split('-');
			let injectionId = id[0];
			if (!map.has(injectionId))
				map.set(injectionId, new Set())
			if (id.length > 1)
				map.get(injectionId).add(id[1])
		}
		return map;
	}
}

